import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { forkJoin, Subscription } from 'rxjs';
import { CustomFieldDto } from 'src/app/data-transfer/entities/custom-field-dto';
import { CompanyDto } from 'src/app/data-transfer/entities/doc-entities/company-dto';
import { DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { CustomFieldApiService } from 'src/app/data-transfer/services/custom-field-api-service';
import { PpwrApiService } from 'src/app/data-transfer/services/ppwr-api.service';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { CoreDataUpdateService } from 'src/app/data-transfer/services/core-data-update-api-service';
import { DeleteItemDialogComponent } from 'src/app/components/dialogs/delete-item-dialog/delete-item-dialog.component';
import { DocProfileDialogComponent, DocProfileDialogData } from 'src/app/components/dialogs/doc-profile-dialog/doc-profile-dialog.component';
import { DialogActions } from 'src/app/model/dictionary';
import { getDialogConfig } from 'src/app/util/dialog-util';

@Component({
  selector: 'app-doc-profile-panel',
  templateUrl: './doc-profile-panel.component.html',
  styleUrls: ['./doc-profile-panel.component.scss'],
})
export class DocProfilePanelComponent implements OnInit, OnDestroy {
  docProfiles: DocProfileDto[] = [];
  availableSystemCustomFields: CustomFieldDto[] = [];
  availableUnitCustomFields: CustomFieldDto[] = [];
  manufacturers: CompanyDto[] = [];

  linkManufacturerProfileId: string | null = null;
  manufacturerSearchControl = new FormControl('', { nonNullable: true });
  private linkManufacturerSnapshot: CompanyDto[] = [];

  private dataSubscription?: Subscription;

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private customFieldApiService: CustomFieldApiService,
    private ppwrApiService: PpwrApiService,
    private userApiService: UserApiService,
    private cd: ChangeDetectorRef,
    private coreDataService: CoreDataUpdateService
  ) {}

  get evidenceTypes() {
    return this.coreDataService.allDOCEvidenceTypes;
  }

  ngOnInit(): void {
    this.dataSubscription = forkJoin({
      systemFields: this.customFieldApiService.getPackagingSystemFields(),
      unitFields: this.customFieldApiService.getPackagingUnitFields(),
      docProfiles: this.ppwrApiService.getAllDocProfiles(),
      organization: this.userApiService.getOwnOrganization(),
    }).subscribe({
      next: ({ systemFields, unitFields, docProfiles, organization }) => {
        this.availableSystemCustomFields = systemFields;
        this.availableUnitCustomFields = unitFields;
        this.docProfiles = docProfiles;
        this.manufacturers = organization.manufacturerProfiles ?? [];
      },
      error: error => {
        console.error('Error while loading DOC profile data:', error);
        this.showSnackbar('ppwr.docProfiles.loadError', 'app-error', 4000);
      },
    });
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }

  addProfile(): void {
    this.openDialog(new DocProfileDto(), true);
  }

  editProfile(profile: DocProfileDto): void {
    this.openDialog(profile, false);
  }

  private openDialog(profile: DocProfileDto, isEditingNew: boolean): void {
    const data = new DocProfileDialogData(
      profile,
      isEditingNew,
      this.docProfiles.filter(p => p.id !== profile.id).map(p => p.profileName),
      this.evidenceTypes,
      this.availableSystemCustomFields,
      this.availableUnitCustomFields
    );

    this.dialog
      .open(DocProfileDialogComponent, this.getPpwrDialogConfig(data, '1200px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        this.persistProfile(result.data);
      });
  }

  private persistProfile(savedProfile: DocProfileDto): void {
    const originalId = savedProfile.id;
    this.ppwrApiService.createOrUpdateDocProfile(savedProfile).subscribe({
      next: saveResult => {
        const persistedProfile: DocProfileDto = { ...savedProfile, id: saveResult.id, version: saveResult.version };
        const index = this.docProfiles.findIndex(p => p.id === originalId);
        this.docProfiles = index === -1 ? [...this.docProfiles, persistedProfile] : this.docProfiles.map((p, i) => (i === index ? persistedProfile : p));
      },
      error: error => {
        console.error('Error while saving DOC profile:', error);
        this.showSnackbar('ppwr.docProfiles.saveError', 'app-error', 4000);
      },
    });
  }

  linkedManufacturerCount(profile: DocProfileDto): number {
    return this.manufacturers.filter(manufacturer => manufacturer.defaultDocProfileId === profile.id).length;
  }

  isLinkManufacturerOpen(profile: DocProfileDto): boolean {
    return this.linkManufacturerProfileId === profile.id;
  }

  openLinkManufacturer(profile: DocProfileDto): void {
    if (this.linkManufacturerProfileId !== null && this.linkManufacturerProfileId !== profile.id) {
      this.manufacturers = this.linkManufacturerSnapshot;
    }
    this.linkManufacturerSnapshot = structuredClone(this.manufacturers);
    this.linkManufacturerProfileId = profile.id;
    this.manufacturerSearchControl.setValue('');
  }

  onLinkManufacturerClosed(profile: DocProfileDto): void {
    if (this.linkManufacturerProfileId !== profile.id) return;
    this.manufacturers = this.linkManufacturerSnapshot;
    this.linkManufacturerProfileId = null;
  }

  cancelLinkManufacturer(): void {
    this.manufacturers = this.linkManufacturerSnapshot;
    this.linkManufacturerProfileId = null;
  }

  filteredManufacturers(): CompanyDto[] {
    const query = this.manufacturerSearchControl.value.trim().toLowerCase();
    if (!query) return this.manufacturers;
    return this.manufacturers.filter(manufacturer => manufacturer.companyName.toLowerCase().includes(query));
  }

  isManufacturerLinked(profile: DocProfileDto, manufacturer: CompanyDto): boolean {
    return manufacturer.defaultDocProfileId === profile.id;
  }

  areAllManufacturersLinked(profile: DocProfileDto): boolean {
    return this.manufacturers.length > 0 && this.manufacturers.every(manufacturer => this.isManufacturerLinked(profile, manufacturer));
  }

  areSomeManufacturersLinked(profile: DocProfileDto): boolean {
    return this.manufacturers.some(manufacturer => this.isManufacturerLinked(profile, manufacturer)) && !this.areAllManufacturersLinked(profile);
  }

  toggleAllManufacturerLinks(profile: DocProfileDto): void {
    const linkAll = !this.areAllManufacturersLinked(profile);
    this.manufacturers.forEach(manufacturer => {
      if (linkAll) {
        manufacturer.defaultDocProfileId = profile.id;
      } else if (this.isManufacturerLinked(profile, manufacturer)) {
        manufacturer.defaultDocProfileId = null;
      }
    });
  }

  toggleManufacturerLink(profile: DocProfileDto, manufacturer: CompanyDto): void {
    manufacturer.defaultDocProfileId = this.isManufacturerLinked(profile, manufacturer) ? null : profile.id;
  }

  hasManufacturerLinkChanges(): boolean {
    const normalize = (companies: CompanyDto[]) => companies.map(company => company.defaultDocProfileId ?? null);
    return JSON.stringify(normalize(this.manufacturers)) !== JSON.stringify(normalize(this.linkManufacturerSnapshot));
  }

  private getChangedManufacturers(): CompanyDto[] {
    return this.manufacturers.filter(manufacturer => {
      const original = this.linkManufacturerSnapshot.find(m => m.companyProfileId === manufacturer.companyProfileId);
      return !original || original.defaultDocProfileId !== manufacturer.defaultDocProfileId;
    });
  }

  saveManufacturerLinks(): void {
    const changedManufacturers = this.getChangedManufacturers();
    if (changedManufacturers.length === 0) {
      this.linkManufacturerProfileId = null;
      return;
    }
    this.userApiService.createOrUpdateManufacturerProfiles(changedManufacturers).subscribe({
      next: () => {
        this.linkManufacturerSnapshot = this.manufacturers;
        this.linkManufacturerProfileId = null;
        this.cd.detectChanges();
      },
      error: error => {
        console.error('Error while updating manufacturer DOC profile links:', error);
        this.showSnackbar('ppwr.docProfiles.saveError', 'app-error', 4000);
        this.manufacturers = this.linkManufacturerSnapshot;
        this.cd.detectChanges();
      },
    });
  }

  confirmDeleteProfile(profile: DocProfileDto): void {
    const dialogData = {
      dialogHeader: this.translocoService.translate('ppwr.docProfiles.deleteHeader'),
      dialogText: this.translocoService.translate('ppwr.docProfiles.deleteText'),
    };
    this.dialog
      .open(DeleteItemDialogComponent, this.getPpwrDialogConfig(dialogData, '500px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.DELETE) return;
        this.ppwrApiService.deleteDocProfile(profile.id, profile.version).subscribe({
          next: () => {
            this.docProfiles = this.docProfiles.filter(p => p.id !== profile.id);
          },
          error: error => {
            console.error('Error while deleting DOC profile:', error);
            this.showSnackbar(this.extractConflictMessage(error, profile) ?? 'ppwr.docProfiles.deleteError', 'app-error', 4000);
          },
        });
      });
  }

  private showSnackbar(messageKey: string, panelClass: string, duration: number): void {
    this.snackBar.open(this.translocoService.translate(messageKey), this.translocoService.translate('common.buttons.close'), {
      duration,
      panelClass: ['app-snackbar', panelClass],
    });
  }

  private extractConflictMessage(error: unknown, profile: DocProfileDto): string | null {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400 || typeof error.error !== 'string') return null;
    return error.error.replaceAll(`with ID ${profile.id}`, `with the name ${profile.profileName}`);
  }

  private getPpwrDialogConfig(data: unknown, width: string): MatDialogConfig {
    const config = getDialogConfig(data, width);
    config.maxWidth = 'calc(100vw - 32px)';
    config.panelClass = 'ppwr-dialog-panel';
    config.restoreFocus = true;
    return config;
  }
}
